import { useEffect, useRef } from 'react';
import { Alert, Backdrop, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import useAuth from '../hooks/useAuth';
import useMaintenanceActive from '@/hooks/useMaintenanceActive';

const DEFAULT_MESSAGE = 'El sistema se encuentra en mantención. Intente nuevamente más tarde.';

// Al bajar la cortina la recarga espera a que venza el cache del guard (TTL de
// 20 s en `functions/src/utils/maintenance.ts`): antes, una instancia tibia aún
// puede responder 503 y el portal de clientes se queda pegado en LoadingScreen.
// Encima se reparte con jitter para que la flota no golpee a la vez las
// functions recién desplegadas (y todavía frías).
const GUARD_CACHE_MS = 20_000;
const RELOAD_JITTER_MS = 10_000;

// Switch de prueba: en true el dev server respeta params/maintenance igual que
// un build (mismo significado que MAINTENANCE_ENFORCE en el backend). Ojo:
// activar el flag en el proyecto dev bloquea en firestore.rules las escrituras
// directas de todo el equipo. Dejar en false salvo mientras se prueba en local.
const ENFORCE_CURTAIN_IN_DEV = false;

// Excepción dev: el dev server local nunca se corta — la mantención es una
// operación de producción y no debe bloquear a quien esté desarrollando.
// `import.meta.env.DEV` es false en cualquier build (el sitio de dev incluido),
// así que solo exime a `npm start`.
const DEV_BYPASS = import.meta.env.DEV && !ENFORCE_CURTAIN_IN_DEV;

/**
 * Curtain de mantención: mientras `params/maintenance` esté activo reemplaza la
 * app por una pantalla bloqueante (no la superpone: así nada sigue montado ni
 * pidiendo datos detrás). Solo los UIDs en `allowedUids` ven un banner de
 * advertencia sobre la app normal y pueden seguir operando.
 *
 * @param {{
 *   maintenance?: {
 *     active?: boolean,
 *     message?: string,
 *     allowedUids?: string[],
 *     since?: import('firebase/firestore').Timestamp | string,
 *     expiresAt?: import('firebase/firestore').Timestamp,
 *   } | null,
 *   children?: import('react').ReactNode,
 * }} props
 */
function MaintenanceCurtain({ maintenance = null, children = null }) {
  const { user } = useAuth();
  const wasActiveRef = useRef(false);

  const flagActive = useMaintenanceActive(maintenance);
  const active = !DEV_BYPASS && flagActive;

  // allowedUids es el ÚNICO bypass, igual que en el guard y en firestore.rules:
  // ningún rol pasa, tampoco f1f (admin lo tiene demasiada gente).
  const bypassed = (maintenance?.allowedUids ?? []).includes(user?.uid ?? '');

  // La ventana de mantención se usa para desplegar, así que al bajarla el
  // bundle que el cliente tiene en memoria quedó viejo: lo recargamos, tanto
  // con el apagado explícito como al vencer expiresAt (el caso de crash).
  // Solo a quien la cortina bloqueó: un usuario de allowedUids siguió
  // trabajando y un reload le borraría el formulario en curso.
  useEffect(() => {
    // Doc ausente, error de lectura o logout (fail-open): la cortina baja pero
    // no recarga — ahí podría entrar en un loop de recargas — y se olvida que
    // estuvo arriba para que el próximo login no recargue en falso.
    if (maintenance === null) {
      wasActiveRef.current = false;
      return undefined;
    }
    if (active) {
      if (!bypassed) wasActiveRef.current = true;
      return undefined;
    }
    if (!wasActiveRef.current) return undefined;
    const delay = GUARD_CACHE_MS + Math.random() * RELOAD_JITTER_MS;
    const timerId = setTimeout(() => window.location.reload(), delay);
    return () => clearTimeout(timerId);
  }, [active, bypassed, maintenance]);

  // Mientras espera el reload, quien estuvo bloqueado sigue viendo la cortina:
  // así no trabaja sobre el bundle viejo ni pega a la API con el guard en cache.
  // (El ref se lee en render a propósito: lo fija el effect del render en que
  // la cortina estaba arriba, y `maintenance !== null` cubre el reset.)
  const reloading = !active && wasActiveRef.current && maintenance !== null;

  // Misma estructura con y sin banner: `children` conserva su posición y no se
  // remonta (perdiendo formularios en curso) al subir o bajar la cortina.
  if ((!active || bypassed) && !reloading) {
    return (
      <>
        {active && (
          <Alert
            severity="warning"
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: (theme) => theme.zIndex.modal + 100,
              borderRadius: 0,
              justifyContent: 'center',
            }}
          >
            Mantención en curso — tu perfil tiene acceso; el resto de los usuarios está bloqueado.
          </Alert>
        )}
        {children}
      </>
    );
  }

  const title = reloading ? 'Mantención finalizada' : 'Sistema en mantención';
  const message = reloading
    ? 'Actualizando a la nueva versión, puede tomar hasta medio minuto…'
    : maintenance?.message || DEFAULT_MESSAGE;
  const since = maintenance?.since?.toDate?.();

  return (
    <Backdrop
      open
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 100,
        backgroundColor: 'rgba(0, 0, 0, 0.88)',
        color: '#fff',
      }}
    >
      <Stack spacing={2} alignItems="center" sx={{ maxWidth: 480, textAlign: 'center', px: 3 }}>
        <Typography variant="h5">{title}</Typography>
        <Typography variant="body1">{message}</Typography>
        {since && !reloading && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Desde {dayjs(since).format('DD-MM-YYYY HH:mm')}
          </Typography>
        )}
      </Stack>
    </Backdrop>
  );
}

export default MaintenanceCurtain;
